import { CancelTokenSource } from "axios";
import { MutationTree } from "vuex";
import { DataSourceResult } from "@/models/DataSourceResult";
import { FilterCollection } from "@/models/FilterCollection";
import { GridColumn } from "@/models/GridColumn";
import { MultiselectItem } from "@/models/MultiselectItem";
import { TableSettings } from "@/models/TableSettings";
import { CampaignEditViewModel, CampaignsPageViewModel } from "@/models/campaigns/Campaign";
import { CampaignGridView, RefreshGridMode } from "@/models/campaigns/CampaignGridView";
import { CampaignsStatisticsByDate } from "@/models/campaigns/CampaignsStatisticsByDate";
import { CampaignStatus } from "@/models/campaigns/CampaignStatus";
import { CreatePhoneViewModel } from "@/models/phones/CreatePhoneViewModel";
import { CampaignsState } from "@/models/states/CampaignsState";
import { CampaignsPayload } from "@/services/api/CampaignsApiService";

const mutations: MutationTree<CampaignsState> = {
  logout(state: CampaignsState) {
    state.selectedCampaignGridColumnsIds = [];
    state.showGraphs = true;
  },

  setCampaignEditSelectedProjectsIds(state: CampaignsState, campaignEditSelectedProjectsIds: number[]) {
    state.campaignEditSelectedProjectsIds = campaignEditSelectedProjectsIds;
  },

  setCampaignEditSelectedTagsIds(state: CampaignsState, campaignEditSelectedTagsIds: number[]) {
    state.campaignEditSelectedTagsIds = campaignEditSelectedTagsIds;
  },

  setCampaignEditViewModel(state: CampaignsState, campaignEditViewModel: CampaignEditViewModel) {
    state.campaignEditViewModel = campaignEditViewModel;
  },

  setCampaigns(state: CampaignsState, campaigns: DataSourceResult) {
    state.campaigns = campaigns;
  },

  setCampaignsCancelTokenSource(state: CampaignsState, cancelTokenSource: CancelTokenSource) {
    state.campaignsCancelTokenSource = cancelTokenSource;
  },

  setCampaignsLoading(state: CampaignsState, campaignsLoading: boolean) {
    state.campaignsLoading = campaignsLoading;
  },

  setCampaignsPageData(state: CampaignsState, campaignsPageData: CampaignsPageViewModel) {
    state.campaignsPageData = campaignsPageData;
  },

  setCampaignsPayload(state: CampaignsState, campaignsPayload: CampaignsPayload) {
    state.campaignsPayload = campaignsPayload;
  },

  setCampaignsStatsByDate(state: CampaignsState, campaignsStatsByDate: CampaignsStatisticsByDate[]) {
    state.campaignsStatsByDate = campaignsStatsByDate;
  },

  setCampaignsWithStats(state: CampaignsState, campaignsWithStats: CampaignGridView[]) {
    state.campaignsWithStats = campaignsWithStats;
  },

  setClients(state: CampaignsState, clients: MultiselectItem[]) {
    state.clients = clients;
  },

  setColumns(state: CampaignsState, columns: GridColumn[]) {
    state.columns = columns;
  },

  setColumnsFilters(state: CampaignsState, columnsFilters: FilterCollection) {
    state.columnsFilters = columnsFilters;
  },

  setCreatePhoneViewModel(state: CampaignsState, createPhoneViewModel: CreatePhoneViewModel) {
    state.createPhoneViewModel = createPhoneViewModel;
  },

  setDatePeriod(state: CampaignsState, datePeriod: Date[]) {
    state.datePeriod = datePeriod;
  },

  setGraphsHiddenOnScroll(state: CampaignsState, graphsHiddenOnScroll: boolean) {
    state.graphsHiddenOnScroll = graphsHiddenOnScroll;
  },

  setIsMediaChannel(state: CampaignsState, isMediaChannel: boolean) {
    state.isMediaChannel = isMediaChannel;
  },

  setNeedToRefreshCampaignsGrid(state: CampaignsState, needToRefreshCampaignsGrid: RefreshGridMode) {
    state.needToRefreshCampaignsGrid = needToRefreshCampaignsGrid;
  },

  setPage(state: CampaignsState, page: number) {
    state.page = page;
  },

  setProjects(state: CampaignsState, projects: MultiselectItem[]) {
    state.projects = projects;
  },

  setSearchValue(state: CampaignsState, searchValue: string) {
    state.searchValue = searchValue;
  },

  setSelectedChannelMetadataTypesIds(state: CampaignsState, selectedChannelMetadataTypesIds: number[]) {
    state.selectedChannelMetadataTypesIds = selectedChannelMetadataTypesIds;
  },

  setSelectedClientsIds(state: CampaignsState, selectedClientsIds: number[]) {
    state.selectedClientsIds = selectedClientsIds;
  },

  setSelectedCampaign(state: CampaignsState, selectedCampaign?: CampaignGridView) {
    state.selectedCampaign = selectedCampaign;
  },

  setSelectedCampaignGridColumnsIds(state: CampaignsState, selectedCampaignGridColumnsIds: number[]) {
    state.selectedCampaignGridColumnsIds = selectedCampaignGridColumnsIds;
  },

  setSelectedCampaignsIds(state: CampaignsState, selectedCampaignsIds: number[]) {
    state.selectedCampaignsIds = selectedCampaignsIds;
  },

  setSelectedCampaignStatus(state: CampaignsState, selectedCampaignStatus?: CampaignStatus | null) {
    state.selectedCampaignStatus = selectedCampaignStatus;
  },

  setSelectedProjectsIds(state: CampaignsState, selectedProjectsIds: number[]) {
    state.selectedProjectsIds = selectedProjectsIds;
  },

  setShowGraphs(state: CampaignsState, showGraphs: boolean) {
    state.showGraphs = showGraphs;
  },

  setSortDir(state: CampaignsState, sortDir: string) {
    state.sortDir = sortDir;
  },

  setSortField(state: CampaignsState, sortField: string) {
    state.sortField = sortField;
  },

  setTableSettings(state: CampaignsState, tableSettings: TableSettings[]) {
    state.tableSettings = tableSettings;
  },
};
export default mutations;
